import logoDark from "@public/logo.svg";
import logoLight from "@public/logo_on_light.svg";
import type { SsoLinkInfoResponse, Translated } from "@repo/shared/types";
import { useQuery } from "@tanstack/react-query";
import { AlertCircle, Link2, UserRound } from "lucide-react";
import { useState, useTransition } from "react";
import { Navigate, useLocation, useSearchParams } from "react-router";
import { FullScreenLoader } from "@/components/full-screen-loader";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { confirmSsoLink, getSsoLinkInfo, SsoLinkError } from "@/lib/api/auth-login";
import { authClient, useSession } from "@/lib/auth-client";
import { useUserLanguage } from "@/lib/i18n/context";
import { useT } from "@/lib/i18n/use-t";
import { ssoRefusalMessage } from "../sso-sign-in-error";
import { T } from "./page.i18n";

/** The values the link texts name. */
type SsoLinkVars = { tenant: string; provider: string; email: string };

type Translations = Translated<typeof T>;

function linkVars(
  info: Pick<SsoLinkInfoResponse, "tenantName" | "ssoName" | "email">,
  t: Translations,
): SsoLinkVars {
  return { tenant: info.tenantName, provider: info.ssoName ?? t.sso, email: info.email };
}

/**
 * QL-350 — the page behind the link-confirmation e-mail. Only the user the
 * e-mail was sent to may confirm, signed in with their password (and 2FA):
 * without a session the page goes to /login and comes back. It shows which
 * IdP identity asked to be linked, so a stranger's attempt can be spotted.
 * Confirming is a button (POST), never the page load, so a mail scanner
 * opening the link cannot confirm it. After confirming, the SSO login starts
 * again and links the account on its way in.
 */
export default function SsoLinkPage() {
  const { userLanguage } = useUserLanguage();
  const t = useT(T);
  const [searchParams] = useSearchParams();
  const location = useLocation();
  const token = searchParams.get("token") ?? "";
  const { data: session, isPending: sessionPending } = useSession();
  const [isPending, startTransition] = useTransition();
  const [error, setError] = useState<string | null>(null);

  const infoQuery = useQuery({
    queryKey: ["sso-link", "info", token, session?.user.id],
    queryFn: () => getSsoLinkInfo(token),
    enabled: token !== "" && !!session,
    retry: false,
  });

  const startSso = async (providerId: string) => {
    const origin = window.location.origin;
    const result = await authClient.signIn.sso({
      providerId,
      callbackURL: `${origin}/`,
      errorCallbackURL: `${origin}/login/sso-error`,
    });
    if (result.error) {
      const code = (result.error as { code?: string }).code;
      setError(ssoRefusalMessage(code, userLanguage) ?? t.failed);
    }
  };

  const handleConfirm = (info: SsoLinkInfoResponse) => {
    setError(null);
    startTransition(async () => {
      try {
        if (!info.confirmed) await confirmSsoLink(token);
        await startSso(info.providerId);
      } catch (err) {
        setError(
          err instanceof SsoLinkError && err.code === "last_password_admin"
            ? t.lastPasswordAdmin(linkVars(info, t))
            : t.failed,
        );
      }
    });
  };

  const loginUrl = `/login?redirect=${encodeURIComponent(location.pathname + location.search)}`;

  const handleSignOut = () => {
    startTransition(async () => {
      await authClient.signOut();
      window.location.assign(loginUrl);
    });
  };

  if (sessionPending) return <FullScreenLoader />;
  if (!session) return <Navigate to={loginUrl} replace />;
  if (token && infoQuery.isPending) return <FullScreenLoader />;

  const info = infoQuery.data;
  const wrongUser =
    infoQuery.error instanceof SsoLinkError && infoQuery.error.code === "wrong_user";

  return (
    <div className="min-h-screen flex items-center justify-center p-4">
      <Card className="w-full max-w-md p-8 space-y-6">
        <div className="flex justify-center">
          <img src={logoLight} alt="Quantfolio" className="h-8 dark:hidden" />
          <img src={logoDark} alt="Quantfolio" className="h-8 hidden dark:block" />
        </div>
        <h1 className="text-xl font-semibold text-center">{t.title}</h1>

        {wrongUser ? (
          <>
            <Alert variant="warning">
              <AlertCircle />
              <AlertDescription className="text-muted-foreground">
                {t.wrongUser(session.user.email)}
              </AlertDescription>
            </Alert>
            <Button
              variant="outline"
              className="w-full"
              disabled={isPending}
              onClick={handleSignOut}
            >
              {t.signOut}
            </Button>
          </>
        ) : !info ? (
          <Alert
            variant="destructive"
            className="border-destructive/40 bg-destructive/10 text-foreground"
          >
            <AlertCircle />
            <AlertDescription className="text-muted-foreground">{t.invalid}</AlertDescription>
          </Alert>
        ) : info.blockedReason === "last_password_admin" ? (
          <Alert variant="warning">
            <AlertCircle />
            <AlertDescription className="text-muted-foreground">
              {t.lastPasswordAdmin(linkVars(info, t))}
            </AlertDescription>
          </Alert>
        ) : (
          <>
            <div className="space-y-3 text-sm">
              <p>{t.intro(linkVars(info, t))}</p>
              <div className="flex items-start gap-3 rounded-md border p-3">
                <UserRound className="mt-0.5 size-4 shrink-0 text-muted-foreground" />
                <div className="min-w-0">
                  <p className="text-xs text-muted-foreground">{t.identity(linkVars(info, t))}</p>
                  <p className="font-medium">
                    {info.identityName ?? t.unknownIdentity(linkVars(info, t))}
                  </p>
                  <p className="truncate text-xs text-muted-foreground" title={info.identityId}>
                    {info.identityId}
                  </p>
                </div>
              </div>
              <Alert variant="info">
                <Link2 />
                <AlertTitle>{t.consequences(linkVars(info, t))}</AlertTitle>
              </Alert>
              <p className="text-muted-foreground">{t.notYou}</p>
            </div>
            {error && (
              <Alert
                variant="destructive"
                className="border-destructive/40 bg-destructive/10 text-foreground"
              >
                <AlertCircle />
                <AlertDescription className="text-muted-foreground">{error}</AlertDescription>
              </Alert>
            )}
            <Button className="w-full" disabled={isPending} onClick={() => handleConfirm(info)}>
              {(info.confirmed ? t.continue : t.confirm)(linkVars(info, t))}
            </Button>
          </>
        )}
      </Card>
    </div>
  );
}
